/**
 * BidOwnerDecisions
 * -----------------
 * The owner decision tracker. Every open call (316L vs carbon steel, N+1 vs
 * single train) sits here with the scope and BOM consequences attached to each
 * option. Record the answer and the change is PROPOSED with its price delta —
 * nothing touches the quote until you click Apply.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, GitBranch, Sparkles, Plus, Trash2, Copy, CheckCircle2, AlertTriangle,
  RotateCcw, ArrowRight, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const money = (v) => `${Number(v) < 0 ? "-" : "+"}$${Math.abs(Number(v) || 0)
  .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  open: { label: "Awaiting owner", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  answered: { label: "Answered — change proposed", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  applied: { label: "Applied to scope & BOM", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export const BidOwnerDecisions = ({ noteId, token, onApplied }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [decisions, setDecisions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [answering, setAnswering] = useState(null);
  const [answerForm, setAnswerForm] = useState({ option_id: "", answered_by: "", note: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [newD, setNewD] = useState({ question: "", why_it_matters: "", asked_of: "" });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/notes/${noteId}/decisions`, { headers });
      setDecisions(r.data?.decisions || []);
      setSummary(r.data?.summary || null);
      setStatus(r.data?.generate_status || null);
      setError(r.data?.generate_error || null);
    } catch { /* panel stays empty */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (status !== "generating") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [status, load]);

  const generate = async () => {
    try {
      await axios.post(`${API}/bid-desk/notes/${noteId}/decisions/generate`, {}, { headers });
      setStatus("generating");
      toast.info("AskIsaac is drafting the owner decision log…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the decision log"); }
  };

  const addDecision = async () => {
    if (!newD.question.trim()) { toast.error("What does the owner have to decide?"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/bid-desk/notes/${noteId}/decisions`, newD, { headers });
      setNewD({ question: "", why_it_matters: "", asked_of: "" });
      setAddOpen(false);
      toast.success("Decision logged");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log that decision"); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("Remove this decision from the log?")) return;
    try {
      await axios.delete(`${API}/bid-desk/notes/${noteId}/decisions/${d.id}`, { headers });
      load();
    } catch { toast.error("Could not remove it"); }
  };

  const submitAnswer = async (d) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bid-desk/notes/${noteId}/decisions/${d.id}/answer`,
        answerForm, { headers });
      const net = r.data?.impact?.net_cost_delta;
      toast.success("Answer recorded — change proposed", {
        description: net ? `Cost basis moves ${money(net)} if you apply it` : "No price change" });
      setAnswering(null);
      setAnswerForm({ option_id: "", answered_by: "", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record that answer"); }
    finally { setBusy(false); }
  };

  const apply = async (d) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bid-desk/notes/${noteId}/decisions/${d.id}/apply`, {}, { headers });
      const a = r.data.applied;
      toast.success("Applied to the scope and BOM", {
        description: `${a.bom_added.length} line(s) added · ${a.bom_removed.length} removed · `
          + `${a.scope_items_added} scope item(s) · cost basis ${money(a.net_cost_delta)}` });
      onApplied?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply that change"); }
    finally { setBusy(false); }
  };

  const reopen = async (d) => {
    try {
      await axios.post(`${API}/bid-desk/notes/${noteId}/decisions/${d.id}/reopen`, {}, { headers });
      toast.info("Back on the open list");
      load();
    } catch { toast.error("Could not reopen it"); }
  };

  const copyEmail = async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/notes/${noteId}/decisions/email`, { headers });
      await navigator.clipboard.writeText(`To: ${r.data.to}\nSubject: ${r.data.subject}\n\n${r.data.body}`);
      toast.success(`${r.data.count} open decision(s) copied`, { description: "Paste into Outlook and send" });
    } catch { toast.error("Could not build the decisions email"); }
  };

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="notes-decisions">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-violet-600" /> Owner decision tracker
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The calls the owner has to make, with what each path does to the scope and the price.
            Record the answer, review the proposed change, then apply it.
          </p>
          {summary && (
            <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold">{summary.total} logged</span>
              {!!summary.open && <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-bold">{summary.open} awaiting owner</span>}
              {!!summary.awaiting_apply && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200 font-bold">{summary.awaiting_apply} to apply</span>}
              {!!summary.applied && <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold">{summary.applied} applied</span>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={copyEmail} data-testid="decisions-copy-email">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy email
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAddOpen((v) => !v)} data-testid="decisions-add-open">
            <Plus className="w-3.5 h-3.5 mr-1" /> Log one
          </Button>
          <Button size="sm" onClick={generate} disabled={status === "generating"}
            className="bg-violet-600 hover:bg-violet-700" data-testid="decisions-generate">
            {status === "generating"
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Draft from the brief
          </Button>
        </div>
      </div>

      {status === "generating" && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> AskIsaac is working the decision forks out of the brief…
        </p>
      )}
      {status === "failed" && error && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {error}
        </p>
      )}

      {addOpen && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/50 p-3" data-testid="decision-add-form">
          <Input value={newD.question} onChange={(e) => setNewD({ ...newD, question: e.target.value })}
            placeholder="e.g. 316L stainless generators or retain the existing carbon steel?"
            className="h-9 text-xs" data-testid="decision-question" />
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            <Input value={newD.why_it_matters} onChange={(e) => setNewD({ ...newD, why_it_matters: e.target.value })}
              placeholder="Why it matters" className="h-9 text-xs" />
            <Input value={newD.asked_of} onChange={(e) => setNewD({ ...newD, asked_of: e.target.value })}
              placeholder="Who answers (name / role)" className="h-9 text-xs" />
          </div>
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={addDecision} disabled={busy}
              className="bg-violet-600 hover:bg-violet-700" data-testid="decision-add-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Log decision
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {decisions.map((d) => {
          const st = STATUS[d.status] || STATUS.open;
          const impact = d.proposed_impact;
          return (
            <div key={d.id} className="rounded-lg border border-slate-200 p-3" data-testid={`decision-${d.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-slate-900">{d.question}</p>
                  {d.why_it_matters && <p className="text-[11px] text-slate-500 mt-0.5">{d.why_it_matters}</p>}
                  {d.context && <p className="text-[11px] text-slate-600 mt-0.5">{d.context}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[9.5px] font-black uppercase tracking-wider border rounded px-2 py-0.5 ${st.cls}`}>
                    {st.label}
                  </span>
                  <button onClick={() => remove(d)} className="text-slate-300 hover:text-red-600"
                    data-testid={`decision-delete-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {!!(d.options || []).length && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2.5">
                  {d.options.map((o) => {
                    const chosen = d.answer?.option_id === o.id;
                    const c = o.changes || {};
                    return (
                      <div key={o.id} className={`rounded-md border p-2.5 text-[11px] ${chosen
                        ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-slate-50/50"}`}
                        data-testid={`decision-option-${o.id}`}>
                        <p className="font-bold text-slate-900 text-[12px]">
                          {o.label}
                          {o.recommended && <span className="ml-1 text-[9px] font-black uppercase text-emerald-700">· we recommend</span>}
                        </p>
                        {o.implications && <p className="text-slate-600 mt-1">{o.implications}</p>}
                        <div className="mt-1.5 space-y-0.5 text-slate-500">
                          {!!(c.bom_add || []).length && <p>+ {c.bom_add.length} BOM line(s)</p>}
                          {!!(c.bom_remove || []).length && <p>− drops {c.bom_remove.join(", ")}</p>}
                          {!!(c.scope_add || []).length && (
                            <p>+ {c.scope_add.reduce((s, x) => s + (x.items || []).length, 0)} scope item(s)</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {d.answer && (
                <div className="mt-2.5 rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-[11px] text-blue-900"
                  data-testid={`decision-answer-${d.id}`}>
                  <p><strong>Owner answered:</strong> {d.answer.label || "—"}
                    {d.answer.answered_by ? ` · ${d.answer.answered_by}` : ""}
                    {d.answer.answered_at ? ` · ${new Date(d.answer.answered_at).toLocaleDateString()}` : ""}</p>
                  {d.answer.note && <p className="mt-0.5">{d.answer.note}</p>}
                  {impact && (
                    <p className="mt-1">
                      Proposed change: {impact.adds.length} line(s) in, {impact.removes.length} out,
                      {" "}{impact.scope_items} scope item(s) ·
                      {" "}<strong>cost basis {money(impact.net_cost_delta)}</strong>
                      {!!(impact.unpriced || []).length && (
                        <span className="text-amber-700"> · unpriced: {impact.unpriced.join(", ")}</span>
                      )}
                    </p>
                  )}
                </div>
              )}

              {d.applied_summary && (
                <p className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                  <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                  Applied {d.applied_at ? new Date(d.applied_at).toLocaleString() : ""} ·
                  {" "}{d.applied_summary.bom_added.length} added,
                  {" "}{d.applied_summary.bom_removed.length} removed,
                  {" "}{d.applied_summary.scope_items_added} scope item(s),
                  {" "}cost basis {money(d.applied_summary.net_cost_delta)}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                {d.status === "open" && (
                  <Button size="sm" variant="outline" data-testid={`decision-answer-open-${d.id}`}
                    onClick={() => { setAnswering(d.id); setAnswerForm({ option_id: (d.options?.[0]?.id) || "", answered_by: d.asked_of || "", note: "" }); }}>
                    Record the owner's answer
                  </Button>
                )}
                {d.status === "answered" && (
                  <>
                    <Button size="sm" onClick={() => apply(d)} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid={`decision-apply-${d.id}`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                      Apply to scope &amp; BOM
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => reopen(d)} className="text-slate-500">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                    </Button>
                  </>
                )}
                {d.status === "applied" && (
                  <Button size="sm" variant="ghost" onClick={() => reopen(d)} className="text-slate-500"
                    data-testid={`decision-reopen-${d.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen (edits stay)
                  </Button>
                )}
              </div>

              {answering === d.id && (
                <div className="mt-2.5 rounded-md border border-violet-200 bg-white p-3" data-testid={`decision-answer-form-${d.id}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">Owner's answer</p>
                    <button onClick={() => setAnswering(null)} className="text-slate-400 hover:text-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {!!(d.options || []).length && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {d.options.map((o) => (
                        <button key={o.id} onClick={() => setAnswerForm({ ...answerForm, option_id: o.id })}
                          className={`rounded-md border px-2.5 py-1.5 text-[11px] font-semibold ${answerForm.option_id === o.id
                            ? "border-violet-500 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600"}`}
                          data-testid={`decision-pick-${o.id}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    <Input value={answerForm.answered_by} className="h-9 text-xs"
                      onChange={(e) => setAnswerForm({ ...answerForm, answered_by: e.target.value })}
                      placeholder="Who gave the answer" />
                    <Input value={answerForm.note} className="h-9 text-xs"
                      onChange={(e) => setAnswerForm({ ...answerForm, note: e.target.value })}
                      placeholder="Note (email date, conditions…)" />
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={() => submitAnswer(d)} disabled={busy}
                      className="bg-violet-600 hover:bg-violet-700" data-testid={`decision-answer-submit-${d.id}`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                      Record &amp; propose the change
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!decisions.length && status !== "generating" && (
          <p className="text-sm text-slate-400">
            Nothing logged yet — "Draft from the brief" pulls the real decision forks out of the
            engineering brief, or log one by hand.
          </p>
        )}
      </div>
    </section>
  );
};

export default BidOwnerDecisions;
